import type { ActivatedRouteSnapshot } from '@angular/router';

import type { RouteBase, RouteData, RouteTitle } from '@kernel';

import type { MenuItem, ScrollOffset } from './nav.types';

// Records overflow positions while the tab is still connected. Removal resets them to 0.
export const captureScroll = (root: HTMLElement | null): readonly ScrollOffset[] => {
  if (!root) return [];

  const saved: ScrollOffset[] = [];

  const visit = (element: HTMLElement): void => {
    const top = element.scrollTop;
    const left = element.scrollLeft;

    if (top || left) saved.push({ element, top, left });
  };

  visit(root);

  for (const element of root.querySelectorAll<HTMLElement>('*')) visit(element);

  return saved;
};

export const restoreScroll = (saved: readonly ScrollOffset[]): void => {
  for (const { element, top, left } of saved) {
    if (!element.isConnected) continue;

    if (top) element.scrollTop = top;
    if (left) element.scrollLeft = left;
  }
};

export const encodeRouteSegments = (segments: readonly string[]): string =>
  segments.filter(Boolean).map(encodeURIComponent).join('/');

export const findLeafSnapshot = (route: ActivatedRouteSnapshot): ActivatedRouteSnapshot => {
  let leaf = route;

  while (leaf.firstChild) leaf = leaf.firstChild;

  return leaf;
};

export const isRootSnapshot = (route: ActivatedRouteSnapshot): boolean =>
  !!(route.routeConfig?.data as RouteData | undefined)?.root;

// Finds the nearest tab root, or returns the route itself.
export const findRootSnapshot = (route: ActivatedRouteSnapshot): ActivatedRouteSnapshot => {
  let node: ActivatedRouteSnapshot | null = route;

  while (node) {
    if (isRootSnapshot(node)) return node;

    node = node.parent;
  }

  return route;
};

// Prefers the deepest route title, then the longest matching menu path.
export const resolveRouteTitle = (
  route: ActivatedRouteSnapshot,
  menuItems: readonly MenuItem[],
): RouteTitle | null => {
  const chain = route.pathFromRoot;
  let params: Record<string, string> = {};

  for (const snapshot of chain) params = { ...params, ...snapshot.params };

  for (let i = chain.length - 1; i >= 0; i--) {
    const data = chain[i]!.data as RouteData;

    if (!data.title) continue;

    const titleParams = data.titleParamKeys?.length
      ? Object.fromEntries(
          data.titleParamKeys.filter((k) => params[k] !== undefined).map((k) => [k, params[k]]),
        )
      : undefined;

    return {
      title: data.title,
      ...(titleParams && Object.keys(titleParams).length ? { titleParams } : {}),
    };
  }

  const path =
    `/${encodeRouteSegments(chain.flatMap((r) => r.url.map((s) => s.path)))}` as RouteBase;

  const findBest = (items: readonly MenuItem[]): MenuItem | null => {
    let best: MenuItem | null = null;

    for (const item of items) {
      if (
        item.path &&
        (path === item.path || path.startsWith(`${item.path}/`)) &&
        (!best || item.path.length > best.path!.length)
      ) {
        best = item;
      }

      if (item.children?.length) {
        const childBest = findBest(item.children);

        if (childBest && (!best || childBest.path!.length > best.path!.length)) best = childBest;
      }
    }

    return best;
  };

  const best = findBest(menuItems);

  return best ? { title: best.title } : null;
};
